import { TasksService } from '../../../../../services/openapi/services/TasksService';
import { TaskImage } from '../../../../../services/openapi/models/TaskImage';
import { TaskImageBase64 } from '../../../../../services/openapi/models/TaskImageBase64';
import { getBackendUrl } from './imageUtils';

/**
 * Rapport-specific helper functions
 */

/**
 * Load task images as base64 data
 */
export const loadTaskImagesBase64 = async (
    taskId: number,
    selectedImageIds?: number[]
): Promise<TaskImageBase64[]> => {
    try {
        const imageIdsParam = selectedImageIds?.length ? selectedImageIds.join(',') : undefined;
        const imagesBase64 = await TasksService.getTaskImagesBase64TaskTaskIdImagesBase64Get(
            taskId,
            imageIdsParam
        );
        return imagesBase64;
    } catch (error) {
        throw error;
    }
};

/**
 * Show image selection dialog and return selected image IDs
 */
export const showImageSelectionDialog = async (taskId: number): Promise<number[]> => {
    try {
        console.log('Loading images for task:', taskId);
        const images = await TasksService.getTaskImagesTaskTaskIdImagesGet(taskId);
        console.log('Loaded images:', images);

        if (images.length === 0) {
            return showNoImagesDialog();
        }

        return showImageSelectDialog(images);
    } catch (error) {
        console.warn('Could not load images for selection:', error);
        return [];
    }
};

/**
 * Show dialog when no images are available
 */
const showNoImagesDialog = (): Promise<number[]> => {
    return new Promise((resolve) => {
        const dialog = createDialogContainer();
        const dialogContent = createDialogContent();

        const title = createTitle('Keine Bilder vorhanden');
        const message = createMessage(
            'Für diese Aufgabe sind keine Bilder vorhanden. Das PDF wird ohne Bilder erstellt.'
        );

        const okButton = createButton('PDF ohne Bilder erstellen', '#2980b9', () => {
            document.body.removeChild(dialog);
            resolve([]);
        });

        dialogContent.appendChild(title);
        dialogContent.appendChild(message);
        dialogContent.appendChild(okButton);
        dialog.appendChild(dialogContent);
        document.body.appendChild(dialog);
    });
};

/**
 * Show dialog for image selection
 */
const showImageSelectDialog = (images: TaskImage[]): Promise<number[]> => {
    return new Promise((resolve) => {
        const dialog = createDialogContainer();
        const dialogContent = createDialogContentWithScroll();

        const title = createTitle(`Bilder für Rapport auswählen (${images.length} verfügbar)`);
        title.style.color = '#2980b9';
        dialogContent.appendChild(title);

        const selectedIds: Set<number> = new Set();

        // Add "Select All" checkbox
        const selectAllContainer = createSelectAllCheckbox(dialogContent, selectedIds);
        dialogContent.appendChild(selectAllContainer);

        // Add image checkboxes
        images.forEach(image => {
            const imageContainer = createImageCheckbox(image, selectedIds);
            dialogContent.appendChild(imageContainer);
        });

        // Add action buttons
        const buttonContainer = createButtonContainer();

        const cancelButton = createButton('Abbrechen', 'white', () => {
            document.body.removeChild(dialog);
            resolve([]);
        }, '#ccc');

        const confirmButton = createButton('PDF mit ausgewählten Bildern erstellen', '#2980b9', () => {
            document.body.removeChild(dialog);
            resolve(Array.from(selectedIds));
        });

        buttonContainer.appendChild(cancelButton);
        buttonContainer.appendChild(confirmButton);
        dialogContent.appendChild(buttonContainer);

        dialog.appendChild(dialogContent);
        document.body.appendChild(dialog);
    });
};

// Dialog component creators

const createDialogContainer = (): HTMLDivElement => {
    const dialog = document.createElement('div');
    dialog.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
    `;
    return dialog;
};

const createDialogContent = (): HTMLDivElement => {
    const content = document.createElement('div');
    content.style.cssText = `
        background: white;
        padding: 30px;
        border-radius: 8px;
        text-align: center;
        max-width: 400px;
    `;
    return content;
};

const createDialogContentWithScroll = (): HTMLDivElement => {
    const content = document.createElement('div');
    content.style.cssText = `
        background: white;
        padding: 20px;
        border-radius: 8px;
        max-width: 600px;
        max-height: 80%;
        overflow-y: auto;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    `;
    return content;
};

const createTitle = (text: string): HTMLHeadingElement => {
    const title = document.createElement('h3');
    title.textContent = text;
    title.style.marginBottom = '15px';
    title.style.color = '#666';
    return title;
};

const createMessage = (text: string): HTMLParagraphElement => {
    const message = document.createElement('p');
    message.textContent = text;
    message.style.marginBottom = '20px';
    message.style.lineHeight = '1.5';
    return message;
};

const createButton = (
    text: string,
    bgColor: string,
    onClick: () => void,
    borderColor?: string
): HTMLButtonElement => {
    const button = document.createElement('button');
    button.textContent = text;
    button.style.cssText = `
        padding: 10px 20px;
        border: ${borderColor ? `1px solid ${borderColor}` : 'none'};
        background: ${bgColor};
        color: ${bgColor === 'white' ? '#333' : 'white'};
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
    `;
    button.addEventListener('click', onClick);
    return button;
};

const createSelectAllCheckbox = (
    dialogContent: HTMLDivElement,
    selectedIds: Set<number>
): HTMLDivElement => {
    const container = document.createElement('div');
    container.style.cssText = `
        margin-bottom: 15px;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 4px;
    `;

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.style.marginRight = '10px';
    checkbox.setAttribute('data-select-all', 'true');

    checkbox.addEventListener('change', () => {
        const checkboxes = dialogContent.querySelectorAll('input[type="checkbox"]:not([data-select-all])');
        checkboxes.forEach((cb: any) => {
            cb.checked = checkbox.checked;
            const imageId = parseInt(cb.dataset.imageId);
            if (checkbox.checked) {
                selectedIds.add(imageId);
            } else {
                selectedIds.delete(imageId);
            }
        });
    });

    const label = document.createElement('label');
    label.textContent = 'Alle Bilder auswählen';
    label.style.fontWeight = 'bold';

    container.appendChild(checkbox);
    container.appendChild(label);
    return container;
};

const createImageCheckbox = (image: TaskImage, selectedIds: Set<number>): HTMLDivElement => {
    const container = document.createElement('div');
    container.style.cssText = `
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        transition: background-color 0.2s;
    `;

    container.addEventListener('mouseenter', () => {
        container.style.backgroundColor = '#f8f9fa';
    });
    container.addEventListener('mouseleave', () => {
        container.style.backgroundColor = 'white';
    });

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.style.marginRight = '10px';
    checkbox.dataset.imageId = image.id.toString();
    checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
            selectedIds.add(image.id);
        } else {
            selectedIds.delete(image.id);
        }
    });

    const thumbnail = createThumbnail(image);

    container.appendChild(checkbox);
    container.appendChild(thumbnail);
    return container;
};

const createThumbnail = (image: TaskImage): HTMLImageElement => {
    const thumbnail = document.createElement('img');
    const backendUrl = getBackendUrl();
    const thumbnailUrl = image.image_path.startsWith('http')
        ? image.image_path
        : `${backendUrl}${image.image_path}`;

    thumbnail.src = thumbnailUrl;
    thumbnail.style.cssText = `
        width: 80px;
        height: 60px;
        object-fit: cover;
        margin-right: 10px;
        border-radius: 4px;
        border: 1px solid #ddd;
    `;

    thumbnail.onerror = () => {
        thumbnail.style.display = 'none';
        const errorDiv = document.createElement('div');
        errorDiv.style.cssText = `
            width: 80px;
            height: 60px;
            margin-right: 10px;
            border-radius: 4px;
            border: 1px solid #ddd;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            color: #999;
            font-size: 12px;
        `;
        errorDiv.textContent = 'Bild nicht verfügbar';
        thumbnail.parentNode?.insertBefore(errorDiv, thumbnail.nextSibling);
    };

    return thumbnail;
};

const createButtonContainer = (): HTMLDivElement => {
    const container = document.createElement('div');
    container.style.cssText = `
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;
        border-top: 1px solid #eee;
        padding-top: 15px;
    `;
    return container;
};